const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  bytes.forEach(b => str += String.fromCharCode(b));
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export async function startPasskeyRegistration() {
  const res = await fetch(`${API}/auth/passkey/register/start`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to start registration');
  const { options } = await res.json();
  const parsed = JSON.parse(options);

  // Convert challenge and user.id from base64url to ArrayBuffer
  parsed.challenge = base64urlToBuffer(parsed.challenge);
  parsed.user.id = base64urlToBuffer(parsed.user.id);
  if (parsed.excludeCredentials) {
    parsed.excludeCredentials = parsed.excludeCredentials.map(c => ({
      ...c,
      id: base64urlToBuffer(c.id),
    }));
  }

  const credential = await navigator.credentials.create({ publicKey: parsed });

  const credentialJSON = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
  };

  if (credential.response.getTransports) {
    credentialJSON.response.transports = credential.response.getTransports();
  }

  const verifyRes = await fetch(`${API}/auth/passkey/register/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ credential: credentialJSON }),
  });
  if (!verifyRes.ok) {
    const err = await verifyRes.json();
    throw new Error(err.detail || 'Verification failed');
  }
  return await verifyRes.json();
}

export async function startPasskeyLogin() {
  const startRes = await fetch(`${API}/auth/passkey/login/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!startRes.ok) throw new Error('Failed to start authentication');
  const { options, challenge_id } = await startRes.json();
  const parsed = JSON.parse(options);

  parsed.challenge = base64urlToBuffer(parsed.challenge);
  if (parsed.allowCredentials) {
    parsed.allowCredentials = parsed.allowCredentials.map(c => ({
      ...c,
      id: base64urlToBuffer(c.id),
    }));
  }

  const assertion = await navigator.credentials.get({ publicKey: parsed });

  const assertionJSON = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      signature: bufferToBase64url(assertion.response.signature),
    },
  };

  if (assertion.response.userHandle) {
    assertionJSON.response.userHandle = bufferToBase64url(assertion.response.userHandle);
  }

  const verifyRes = await fetch(`${API}/auth/passkey/login/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ credential: assertionJSON, challenge_id }),
  });
  if (!verifyRes.ok) {
    const err = await verifyRes.json();
    throw new Error(err.detail || 'Authentication failed');
  }
  return await verifyRes.json();
}

export function isPasskeySupported() {
  return !!(window.PublicKeyCredential && navigator.credentials);
}
